import { useEffect, useMemo, useState } from 'react';
import { copyDayShifts, type CopyDayResult, type CopyDaySkipped } from '@/lib/scheduling';

/**
 * CopyDayDialog — intra-month day copy (0107).
 *
 * Given a "source" day that already has shifts, let the user pick one or
 * more target days in the SAME month and copy the source's shifts onto
 * each of them. Backend (schedule_copy_day) enforces the hard rules —
 * this UI just makes the common cases fast:
 *   - Disables the source day (can't copy onto itself)
 *   - Disables days that already have shifts (backend would reject the
 *     whole op, so we prevent that upfront)
 *   - Marks closed days with a warning icon (still selectable; backend
 *     doesn't refuse because "closed" is a soft signal, not a hard rule)
 *   - Quick-pick: "All {sourceDow}s in this month" (all other same-weekday
 *     dates that are enabled)
 *   - After submit, shows copied_count + any shifts that got skipped due
 *     to an approved time-off overlap.
 */

export interface CopyDayDialogProps {
  open: boolean;
  locationId: string;
  sourceDate: string;             // 'YYYY-MM-DD'
  sourceShiftCount: number;       // for the summary line
  monthStart: string;             // 'YYYY-MM-01' — bounds the picker
  occupiedDates: Set<string>;     // dates (excl. sourceDate) with any non-cancelled shifts
  closedDates: Set<string>;       // location-closed dates in the month
  onClose: () => void;
  onDone: () => void | Promise<void>;
}

export function CopyDayDialog(props: CopyDayDialogProps) {
  const {
    open, locationId, sourceDate, sourceShiftCount, monthStart,
    occupiedDates, closedDates, onClose, onDone,
  } = props;

  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [overrideConflicts, setOverrideConflicts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CopyDayResult | null>(null);

  // Reset on open.
  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setOverrideConflicts(false);
    setError(null);
    setResult(null);
    setSubmitting(false);
  }, [open, sourceDate]);

  // Build the day grid for this month (days 1..N of monthStart).
  const cells = useMemo(() => buildMonthCells(monthStart), [monthStart]);

  const sourceDow = useMemo(() => dowOf(sourceDate), [sourceDate]);
  const sourceDowName = DOW_NAMES[sourceDow];

  const eligible = useMemo(() => {
    // A date is eligible for selection if it's inside the month, not the
    // source, and not already occupied. Padding cells (isOutOfMonth) from
    // adjacent months must be excluded — the backend would reject them
    // with 'target_dates_outside_schedule_month'.
    return cells
      .filter((c) => !c.isOutOfMonth && c.date !== sourceDate && !occupiedDates.has(c.date))
      .map((c) => c.date);
  }, [cells, sourceDate, occupiedDates]);

  const sameDowEligible = useMemo(
    () => eligible.filter((d) => dowOf(d) === sourceDow),
    [eligible, sourceDow]
  );

  function toggle(date: string) {
    if (date === sourceDate) return;
    if (occupiedDates.has(date)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date); else next.add(date);
      return next;
    });
  }

  function selectAllSameDow() {
    setSelected(new Set(sameDowEligible));
  }
  function clear() { setSelected(new Set()); }

  async function onConfirm() {
    if (selected.size === 0) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const targetDates = Array.from(selected).sort();
      const res = await copyDayShifts(locationId, sourceDate, targetDates, overrideConflicts);
      setResult(res);
      if (res.ok) {
        await onDone();
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  const selectedCount = selected.size;
  const projectedCopies = selectedCount * sourceShiftCount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl rounded-lg border border-border bg-card shadow-lg">
        <header className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h2 className="text-base font-medium">Copy day</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Copy the {sourceShiftCount} shift{sourceShiftCount === 1 ? '' : 's'} on{' '}
              <strong>{formatDayLabel(sourceDate)}</strong> onto other days in this month.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">×</button>
        </header>

        <div className="p-4 space-y-4">
          {sourceShiftCount === 0 ? (
            <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              This day has no shifts to copy.
            </p>
          ) : (
            <>
              {/* Quick picks */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">Quick pick:</span>
                <button
                  type="button"
                  onClick={selectAllSameDow}
                  disabled={sameDowEligible.length === 0}
                  className="text-xs px-2 py-1 rounded border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                  title={sameDowEligible.length === 0 ? 'No other eligible ' + sourceDowName + 's this month' : ''}
                >
                  All {sourceDowName}s ({sameDowEligible.length})
                </button>
                {selectedCount > 0 && (
                  <button type="button" onClick={clear} className="text-xs px-2 py-1 rounded border border-border hover:bg-muted">
                    Clear selection
                  </button>
                )}
              </div>

              {/* Month grid */}
              <div className="rounded-md border border-border overflow-hidden">
                <div className="grid grid-cols-7 bg-muted/40">
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d) => (
                    <div key={d} className="px-2 py-1 text-center text-[10px] font-medium text-muted-foreground border-b border-border">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {cells.map((c) => {
                    const isSource   = c.date === sourceDate;
                    const isOccupied = occupiedDates.has(c.date) && !isSource;
                    const isClosed   = closedDates.has(c.date);
                    const isChecked  = selected.has(c.date);
                    const isOutOfMonth = c.isOutOfMonth;
                    const disabled   = isOutOfMonth || isSource || isOccupied;

                    let label = '';
                    if (isSource)   label = 'Source day (cannot copy onto itself)';
                    else if (isOccupied) label = 'This day already has shifts — clear it first';
                    else if (isClosed)   label = 'Location is closed on this day (copy will still succeed but shifts will fall outside hours)';

                    return (
                      <button
                        key={c.date}
                        type="button"
                        onClick={() => toggle(c.date)}
                        disabled={disabled}
                        title={label}
                        aria-pressed={isChecked}
                        className={
                          'relative h-14 border-r border-b border-border last:border-r-0 text-left px-2 py-1 text-xs ' +
                          (isOutOfMonth ? 'bg-muted/20 text-muted-foreground/40 cursor-default ' : '') +
                          (isSource ? 'bg-blue-50 text-blue-800 ring-2 ring-inset ring-blue-400 cursor-not-allowed ' : '') +
                          (isOccupied ? 'bg-muted/40 text-muted-foreground line-through cursor-not-allowed ' : '') +
                          (isChecked && !disabled ? 'bg-emerald-50 ring-2 ring-inset ring-emerald-500 ' : '') +
                          (!disabled && !isChecked ? 'hover:bg-muted/40 ' : '')
                        }
                      >
                        <div className="flex items-start justify-between">
                          <span className="tabular-nums font-medium">{c.day}</span>
                          {isChecked && <span className="text-emerald-700 text-xs" aria-hidden="true">✓</span>}
                          {isSource && <span className="text-[9px] uppercase font-semibold text-blue-700">src</span>}
                        </div>
                        {isClosed && !isOutOfMonth && !isSource && !isOccupied && (
                          <span className="absolute bottom-1 right-1 text-[9px] text-amber-700" title="Closed day">⚠</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
                <LegendSwatch cls="bg-blue-50 ring-1 ring-blue-400">Source</LegendSwatch>
                <LegendSwatch cls="bg-emerald-50 ring-1 ring-emerald-500">Selected</LegendSwatch>
                <LegendSwatch cls="bg-muted/40">Has shifts (blocked)</LegendSwatch>
                <LegendSwatch cls="bg-card border border-border">Closed (warn)</LegendSwatch>
              </div>

              {/* Preview */}
              <div className="rounded-md border border-border bg-muted/20 p-3 text-sm">
                {selectedCount === 0 ? (
                  <p className="text-muted-foreground">Pick one or more target days above.</p>
                ) : overrideConflicts ? (
                  <p>
                    Will insert <strong>{projectedCopies}</strong> shift{projectedCopies === 1 ? '' : 's'} across{' '}
                    <strong>{selectedCount}</strong> day{selectedCount === 1 ? '' : 's'}, <strong>overriding</strong> any
                    approved time-off or inactive-staff conflicts. The copied shifts will be marked with an override reason
                    for audit.
                  </p>
                ) : (
                  <p>
                    Will insert up to <strong>{projectedCopies}</strong> shift{projectedCopies === 1 ? '' : 's'} across{' '}
                    <strong>{selectedCount}</strong> day{selectedCount === 1 ? '' : 's'}. Shifts assigned to staff on approved
                    time-off or inactive on the target date will be <strong>skipped</strong> and reported after copy.
                  </p>
                )}
              </div>

              {/* Override option. Structure: sibling input + label, with the
                  input receiving the id and the label pointing at it via
                  htmlFor. Wrapping the input inside a label with mixed
                  text-and-input children was tripping jsx-a11y's
                  label-has-associated-control rule. */}
              <div className="flex items-start gap-2 rounded-md border border-border bg-card p-3 text-sm hover:bg-muted/20">
                <input
                  id="copy-day-override-conflicts"
                  type="checkbox"
                  checked={overrideConflicts}
                  onChange={(e) => setOverrideConflicts(e.target.checked)}
                  className="mt-0.5 cursor-pointer"
                />
                <label htmlFor="copy-day-override-conflicts" className="cursor-pointer flex-1">
                  <strong>Override conflicts and copy anyway</strong>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    Insert every shift even if the assigned staff is on approved time-off or inactive on that date. Copied
                    shifts will carry an override reason for audit.
                  </span>
                </label>
              </div>
            </>
          )}

          {error && <p className="rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive">{error}</p>}

          {result && !result.ok && (
            <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive space-y-1">
              <p className="font-medium">Copy blocked: {humanizeCopyError(result.error)}</p>
              {result.conflict_dates && result.conflict_dates.length > 0 && (
                <p className="text-xs">
                  Conflicting day{result.conflict_dates.length === 1 ? '' : 's'}:{' '}
                  {result.conflict_dates.map(formatDayLabel).join(', ')}
                </p>
              )}
            </div>
          )}

          {result && result.ok && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 space-y-2">
              <p>
                Copied <strong>{result.copied_count}</strong> shift{result.copied_count === 1 ? '' : 's'} across{' '}
                <strong>{result.target_count}</strong> day{result.target_count === 1 ? '' : 's'}
                {result.override_conflicts ? ' (conflicts overridden)' : ''}.
              </p>
              {result.skipped && result.skipped.length > 0 && (
                <div className="text-xs">
                  <p className="font-medium">
                    Skipped {result.skipped.length} shift{result.skipped.length === 1 ? '' : 's'}:
                  </p>
                  <ul className="mt-1 space-y-0.5 list-disc list-inside">
                    {result.skipped.slice(0, 12).map((s, i) => (
                      <li key={i}>
                        <strong>{s.staff_name}</strong> — {formatDayLabel(s.date)} — {formatSkipReason(s)}
                      </li>
                    ))}
                    {result.skipped.length > 12 && <li>…and {result.skipped.length - 12} more</li>}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="flex justify-end gap-2 p-4 border-t border-border">
          <button type="button" onClick={onClose} className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40">
            {result?.ok ? 'Close' : 'Cancel'}
          </button>
          {!result?.ok && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={submitting || selectedCount === 0 || sourceShiftCount === 0}
              className={
                'rounded-md px-4 py-1.5 text-sm font-medium disabled:opacity-50 ' +
                (overrideConflicts
                  ? 'bg-amber-600 text-white hover:bg-amber-700'
                  : 'bg-primary text-primary-foreground hover:opacity-90')
              }
            >
              {submitting
                ? 'Copying…'
                : overrideConflicts
                  ? `Copy to ${selectedCount} day${selectedCount === 1 ? '' : 's'} (override conflicts)`
                  : `Copy to ${selectedCount} day${selectedCount === 1 ? '' : 's'}`}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

// ----- helpers --------------------------------------------------------------

const DOW_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

function LegendSwatch({ cls, children }: { cls: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={'inline-block h-3 w-3 rounded-sm ' + cls} aria-hidden="true" />
      <span>{children}</span>
    </span>
  );
}

function dowOf(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

function buildMonthCells(monthStart: string) {
  const [y, m] = monthStart.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const dowOfFirst = first.getDay();
  const start = new Date(y, m - 1, 1 - dowOfFirst);
  const cells: { date: string; day: number; dow: number; isOutOfMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    cells.push({
      date, day: d.getDate(), dow: d.getDay(), isOutOfMonth: d.getMonth() !== m - 1,
    });
  }
  return cells;
}

function formatDayLabel(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatSkipReason(s: CopyDaySkipped): string {
  if (s.reason === 'approved_time_off') {
    const typeLabel = s.time_off_type
      ? s.time_off_type.charAt(0).toUpperCase() + s.time_off_type.slice(1).replace(/_/g, ' ')
      : 'Time off';
    return `${typeLabel} (approved time-off)`;
  }
  if (s.reason === 'staff_inactive_on_date') {
    return 'Staff inactive on this date';
  }
  return String(s.reason);
}

function humanizeCopyError(code: string | undefined): string {
  switch (code) {
    case 'target_dates_have_existing_shifts':
      return 'one or more target days already have shifts. Clear them first.';
    case 'source_date_cannot_be_target':
      return 'the source day was in the target list.';
    case 'target_dates_outside_schedule_month':
      return 'a target date is outside this schedule’s month.';
    case 'source_schedule_not_found':
      return 'no draft schedule found for this day.';
    default:
      return code ?? 'unknown error.';
  }
}
